import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Truck, Package, LogOut, User, ClipboardList, Navigation, Car, X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';

const DriverSidebar = ({ mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const { logout, user } = useAuth();

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/chauffeur/dashboard' },
    { icon: ClipboardList, label: 'Mes Missions', path: '/chauffeur/missions' },
    { icon: Navigation, label: 'Mission en cours', path: '/chauffeur/mission-active' },
    { icon: Car, label: 'Ma Flotte', path: '/chauffeur/vehicules' },
    { icon: User, label: 'Mon Profil', path: '/chauffeur/profil' }
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="p-4 lg:p-6 border-b border-white/10 flex items-center justify-between">
        <Link to="/chauffeur/dashboard" onClick={onMobileClose} className="flex items-center gap-3">
          <div className="bg-[#1db584]/20 p-2 rounded-lg backdrop-blur-sm flex-shrink-0">
            <Truck className="w-5 h-5 lg:w-6 lg:h-6 text-[#1db584]" />
          </div>
          <div>
            <h1 className="text-lg lg:text-xl font-bold text-white">Baobab Connect</h1>
            <p className="text-xs text-[#1db584] font-medium">Espace Chauffeur</p>
          </div>
        </Link>
        <button onClick={onMobileClose} className="lg:hidden text-white/70 hover:text-white p-1 rounded hover:bg-white/10">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* User Info */}
      <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-[#1db584]/20 flex items-center justify-center flex-shrink-0">
            <span className="text-[#1db584] font-semibold text-sm">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-white font-medium text-sm truncate">{user?.firstName} {user?.lastName}</p>
            <p className="text-gray-400 text-xs truncate">
              {user?.accountType === 'company' ? user?.companyName : 'Chauffeur indépendant'}
            </p>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 p-3 lg:p-4 space-y-1.5 lg:space-y-2 overflow-y-auto scrollbar-hide">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onMobileClose}
              className={cn(
                'flex items-center gap-3 px-4 py-2.5 lg:py-3 rounded-lg transition-all duration-200 group',
                active ? 'bg-[#1db584]/20 text-[#1db584] shadow-md' : 'text-gray-400 hover:bg-white/5 hover:text-white'
              )}
            >
              <Icon className={cn('w-5 h-5 flex-shrink-0', active ? 'text-[#1db584]' : 'text-gray-400 group-hover:text-white')} />
              <span className="font-medium text-sm">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-3 lg:p-4 border-t border-white/10">
        <button
          onClick={logout}
          className="flex items-center gap-3 px-4 py-2.5 lg:py-3 rounded-lg text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-all duration-200 w-full group"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium text-sm">Déconnexion</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onMobileClose} />
      )}
      <aside className={cn(
        "w-64 bg-gradient-to-b from-[#1a1a2e] to-[#16213e] h-screen fixed left-0 top-0 flex flex-col shadow-xl transition-transform duration-300 z-50",
        mobileOpen ? "translate-x-0" : "-translate-x-full",
        "lg:translate-x-0"
      )}>
        {sidebarContent}
      </aside>
    </>
  );
};

export default DriverSidebar;
